import { loadBaseCSS } from '../shared/button-utils.js';
import { primitiveEvent, primitiveStyles } from '../shared/primitive-utils.js';

export interface ErrorSummaryItem { id:string; message:string; }
let summaryId=0;
/**
 * A focusable error overview with links to labelled fields.
 * @element qds-error-summary
 * @attr {string} label - Visible heading and alert name; default Check these fields.
 * @property {ReadonlyArray<{id: string; message: string}>} items - One error per target ID. Targets may be native or QDS fields.
 * @fires {{id: string; message: string; index: number}} qds-error-focus - Cancelable field-navigation request; prevent default to reveal a step and focus via an integration API.
 * @csspart summary - Named alert; hidden when there are no errors.
 * @csspart heading - Error overview heading.
 * @csspart list - Error links.
 * @csspart link - Target navigation link.
 */
export class QdsErrorSummary extends HTMLElement {
  static get observedAttributes():string[]{return ['label'];}
  private _items:ErrorSummaryItem[]=[];
  private _id=`qds-error-summary-${++summaryId}`;
  private _onClick=(event:Event):void=>{
    const anchor=event.composedPath().find(node=>node instanceof HTMLAnchorElement&&node.hasAttribute('data-error-index')) as HTMLAnchorElement|undefined;
    if(!anchor||!this.shadowRoot!.contains(anchor))return;
    event.preventDefault();
    const index=Number(anchor.dataset.errorIndex),item=this._items[index];
    if(!item||!primitiveEvent(this,'qds-error-focus',{...item,index},true))return;
    const root=this.getRootNode() as Document|ShadowRoot;
    const target=root.getElementById(item.id)||this.ownerDocument.getElementById(item.id);
    if(!(target instanceof HTMLElement)||(target as HTMLInputElement).disabled||target.closest('[hidden],[inert]'))return;
    target.focus();target.scrollIntoView?.({block:'nearest'});
  };
  constructor(){
    super();
    this.attachShadow({mode:'open'}).innerHTML=`<style>${primitiveStyles}
      :host {display:block;}
      [part=summary] {border:1px solid var(--theme-border-primary,#e0e0e0);border-inline-start:4px solid var(--theme-error,#8b1f00);
        border-radius:4px;padding:1rem;color:var(--theme-text-primary,#333);background:var(--theme-background-primary,white);}
      h2 {font-size:1.125rem;margin:0 0 .5rem;}
      ul {margin:0;padding-inline-start:1.25rem;}
      a {display:inline-flex;align-items:center;min-height:44px;padding:.25rem;color:var(--theme-text-primary,#333);text-underline-offset:.15em;overflow-wrap:anywhere;}
      a:focus-visible,[part=summary]:focus-visible {outline:2px solid var(--theme-accent-primary,#445e89);outline-offset:2px;}
      @media(forced-colors:active){[part=summary]{border-color:CanvasText;}}
    </style><section part="summary" role="alert" tabindex="-1" aria-labelledby="${this._id}-heading" hidden><h2 part="heading" id="${this._id}-heading"></h2><ul part="list"></ul></section>`;
    loadBaseCSS();
  }
  get items():ErrorSummaryItem[]{return this._items.map(item=>({...item}));}
  set items(value:ReadonlyArray<ErrorSummaryItem>){
    const ids=new Set<string>();
    this._items=Array.isArray(value)?value.flatMap(item=>{
      if(!item||typeof item.id!=='string'||!item.id.trim()||typeof item.message!=='string'||!item.message.trim()||ids.has(item.id.trim()))return [];
      const id=item.id.trim();ids.add(id);return [{id,message:item.message.trim()}];
    }):[];
    this._sync();
  }
  get label():string{return this.getAttribute('label')||'Check these fields';}
  set label(value:string){this.setAttribute('label',value??'');}
  connectedCallback():void{
    for(const name of ['items','label'] as const)if(Object.prototype.hasOwnProperty.call(this,name)){
      const value=this[name];Reflect.deleteProperty(this,name);Reflect.set(this,name,value);
    }
    this.shadowRoot!.addEventListener('click',this._onClick);this._sync();
  }
  disconnectedCallback():void{this.shadowRoot!.removeEventListener('click',this._onClick);}
  attributeChangedCallback():void{if(this.isConnected)this._sync();}
  /** Focus the named overview after populating errors. Empty summaries do not take focus. */
  override focus(options?:FocusOptions):void{if(this._items.length)this.shadowRoot!.querySelector<HTMLElement>('[part=summary]')!.focus(options);}
  private _sync():void{
    const root=this.shadowRoot!,summary=root.querySelector<HTMLElement>('[part=summary]')!,list=root.querySelector('ul')!;
    const focused=root.activeElement as HTMLElement|null;
    root.querySelector('h2')!.textContent=this.label;summary.hidden=!this._items.length;
    const nodes=this._items.map((item,index)=>{
      const li=document.createElement('li'),anchor=document.createElement('a');anchor.setAttribute('part','link');anchor.href=`#${encodeURIComponent(item.id)}`;
      anchor.dataset.errorIndex=String(index);anchor.dataset.errorId=item.id;anchor.textContent=item.message;li.append(anchor);return li;
    });
    list.replaceChildren(...nodes);
    if(focused?.hasAttribute('data-error-id')&&this._items.length){
      ([...list.querySelectorAll('a')].find(anchor=>anchor.dataset.errorId===focused.dataset.errorId)||summary).focus({preventScroll:true});
    }
  }
}
if(!customElements.get('qds-error-summary'))customElements.define('qds-error-summary',QdsErrorSummary);
